import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { Branch } from '@/types/branches';

interface MapComponentProps {
  mapHeight: string;
}

async function fetchBranches(): Promise<Branch[]> {
  const apiUrl = import.meta.env.VITE_API_URL || import.meta.env.NEXT_PUBLIC_API_URL || ''
  const res = await fetch(`${apiUrl}/v1/branches`);
  if (!res.ok) throw new Error('Failed to fetch branches');
  const json = await res.json();
  return json.data;
}

export default function MapComponent({ mapHeight }: MapComponentProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const [loaded, setLoaded] = useState(false);
  const userMarkerRef = useRef<any>(null);


  const { data: branches, isLoading: BranchesLoader } = useQuery({
    queryKey: ['branches-for-map'],
    queryFn: fetchBranches,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
console.log("is this the branch: ", branches)
  
  useEffect(() => {
    // load leaflet CSS & JS from CDN
    if (!document.querySelector('link[data-leaflet]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      link.setAttribute('data-leaflet', '1');
      document.head.appendChild(link);
    }
    if (!document.querySelector('script[data-leaflet]')) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.setAttribute('data-leaflet', '1');
      script.async = true;
      script.onload = () => setLoaded(true);
      document.body.appendChild(script);
    } else {
      setLoaded(true);
    }
  }, []);

  // Handle resize
  useEffect(() => {
    if (mapRef.current) {
      // Small delay to allow container to resize before invalidating map size
      const timer = setTimeout(() => {
        mapRef.current.invalidateSize();
      }, 210);
      return () => clearTimeout(timer);
    }
  }, [mapHeight ]);
  // Handle locateUser event
  useEffect(() => {
    const handleLocate = (e: CustomEvent) => {
      if (!mapRef.current || !loaded) return;
      const { lat, lng } = e.detail;
      const L = (window as any).L;
      
      mapRef.current.flyTo([lat, lng], 18, { duration: 1.5, easeLinearity: 0.25 });

      if (userMarkerRef.current) {
        userMarkerRef.current.setLatLng([lat, lng]);
      } else {
        const userIcon = L.divIcon({
          className: 'user-location-marker',
          html: `<div class="relative w-4 h-4">
                  <div class="absolute inset-[-8px] bg-blue-500/30 rounded-full animate-ping"></div>
                  <div class="absolute inset-0 bg-blue-500 border-2 border-white rounded-full shadow-md"></div>
                 </div>`,
          iconSize: [16, 16],
          iconAnchor: [8, 8],
        });
        userMarkerRef.current = L.marker([lat, lng], { icon: userIcon, zIndexOffset: 1000 }).addTo(mapRef.current);
      }
    };

    window.addEventListener('locateUser', handleLocate as EventListener);
    return () => window.removeEventListener('locateUser', handleLocate as EventListener);
  }, [loaded, branches]);

  useEffect(() => {
    console.log('Initializing map...');
    if (!loaded || !mapContainer.current) return;
    const L = (window as any).L;
    if (!L) return;

    // create map only once
    console.log('Creating map instance...');
    if (!mapRef.current) {
      mapRef.current = L.map(mapContainer.current, { 
        zoomControl: false, 
        attributionControl: false,
        zoomAnimation: true,
        fadeAnimation: true,
        markerZoomAnimation: true
      }).setView([9.0244, 38.7468], 15);
      
      L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        attribution: '© OpenStreetMap contributors © CARTO',
        maxZoom: 19,
      }).addTo(mapRef.current);
    }

    // Render branches
    console.log('Rendering branches on map...', mapRef);
    if (branches && mapRef.current) {
      console.log("Full branches data:", branches);
      const L = (window as any).L;
      
      // remove prev layer if any
      if (mapRef.current._branchLayer) {
        try { mapRef.current.removeLayer(mapRef.current._branchLayer); } catch {}
      }
      const markers = L.layerGroup();

      branches.forEach((branch) => {
        console.log('Rendering branch:', branch.location?.coordinates);
        if (!branch?.location?.coordinates?.coordinates) return;

        const [lng, lat] = branch.location.coordinates.coordinates;
        const name = branch.name;
        const firstLetter = name.charAt(0).toUpperCase();

        const divIcon = L.divIcon({
          className: 'custom-marker',
          html: `<div class="group relative flex flex-col items-center justify-center w-12 h-12 transition-transform hover:scale-110">
                   <div class="relative w-12 h-12 drop-shadow-md">
                     <svg viewBox="0 0 384 512" class="w-full h-full fill-pink-600">
                       <path d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0z"/>
                     </svg>
                     <div class="absolute top-0 left-0 w-full h-[70%] flex items-center justify-center">
                       <div class="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                         <span class="text-pink-600 font-bold text-sm leading-none">${firstLetter}</span>
                       </div>
                     </div>
                   </div>
                   <div class="mt-1 bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-md text-pink-600 font-bold text-xs text-center shadow-sm border border-pink-100 whitespace-nowrap">
                     ${name}
                   </div>
                 </div>`,
          iconSize: [48, 64],
          iconAnchor: [24, 48],
          popupAnchor: [0, -48],
        });

        const popupContent = `
          <div class="font-sans p-1 min-w-[160px]">
            <h3 class="font-bold text-sm text-gray-900 mb-1">${name}</h3>
            <div class="flex items-center gap-1.5">
              <span class="w-2 h-2 rounded-full bg-green-500"></span>
              <p class="text-xs text-gray-600 m-0">Open</p>
            </div>
          </div>
        `;
        
        const m = L.marker([lat, lng], { icon: divIcon })
          .bindPopup(popupContent, {
            closeButton: false,
            className: 'custom-popup',
            offset: [0, -10]
          });
          
        m.on('mouseover', function(this: any) { this.openPopup(); });
        m.on('mouseout', function(this: any) { this.closePopup(); });
        
        markers.addLayer(m);
      });
      markers.addTo(mapRef.current);
      mapRef.current._branchLayer = markers;

      // fit bounds if we have branches and it's the first load (optional, or always?)
      // Let's fit bounds only if we haven't moved the map manually? 
      // For now, just fit bounds on data load.
      const validBranches = branches.filter(b => b.location?.coordinates?.coordinates);
      if (validBranches.length > 0) {
        const bounds = L.latLngBounds(validBranches.map(b => {
          const [lng, lat] = b.location.coordinates.coordinates;
          return [lat, lng];
        }));
        mapRef.current.fitBounds(bounds.pad(0.15), { maxZoom: 16 });
      }
    }
  }, [loaded]);

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'absolute', top: 0, left: 0, right: 0, height: mapHeight, zIndex: 0, transition: 'height 200ms ease' }} 
      className="w-full bg-gray-100"
    >
      {/* {!BranchesLoader ? <div> */}
        <div ref={mapContainer} className="w-full h-full" />
       <style>{`
         .leaflet-popup-content-wrapper { border-radius: 16px; box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1); padding: 0; overflow: hidden; }
         .leaflet-popup-content { margin: 12px 16px; }
         .leaflet-popup-tip { box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1); }
         `}</style>
      {/* </div> : <h1 className='z-50'>Loading...</h1>} */}
      
    </motion.div>
  );
}